/**
 * Flights — read-only dashboard over the flight-tracker database (CHO-559).
 *
 * Data flow only: all rendering lives in prop-driven components under
 * components/flights/ (see the /dev/components gallery).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheapestItinerariesTable } from '../components/flights/CheapestItinerariesTable';
import { FlightPriceChart } from '../components/flights/FlightPriceChart';
import { FlightMoversList } from '../components/flights/FlightMoversList';
import { FlightPairSelect } from '../components/flights/FlightPairSelect';
import { pairKey, type FlightPair } from '../components/flights/flightPair';
import { FlightRunHealth } from '../components/flights/FlightRunHealth';
import { FlightFilterBar } from '../components/flights/FlightFilterBar';
import { deriveRunFreshness } from '../components/flights/observationFreshness';
import {
  defaultFilterState,
  filterFlightRows,
  groupFilteredRows,
  reconcileFilterState,
  type FlightFilterOptions,
  type FlightFilterState,
} from '../components/flights/flightFilters';
import { tripShape } from '../../../server/lib/flight-shape.ts';
import type {
  CheapestShapeGroup,
  FlightMover,
  FlightObservation,
  PriceSeries,
  TrackerRun,
  WatchSummary,
} from '../types/flights';

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

interface SummaryResponse {
  available: boolean;
  reason?: string;
  watches?: WatchSummary[];
  runs?: TrackerRun[];
}

interface AnnotatedFlightObservation extends FlightObservation {
  leg_direction: 'outbound' | 'inbound';
  effective_outbound_date: string | null;
  effective_inbound_date: string | null;
}

interface LatestResponse {
  available: boolean;
  rows: AnnotatedFlightObservation[];
  groups: CheapestShapeGroup[];
  per_shape: number;
  filters: FlightFilterOptions;
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <h2 className="text-sm font-semibold text-gray-800 mb-3">{title}</h2>
      {children}
    </section>
  );
}

export default function Flights() {
  const summary = useQuery({
    queryKey: ['flights', 'summary'],
    queryFn: () => getJson<SummaryResponse>('/api/flights/summary'),
    refetchInterval: 5 * 60_000,
  });

  const watches = summary.data?.watches ?? [];
  const [watchSel, setWatchSel] = useState<string | null>(null);
  const watch = watchSel ?? watches[0]?.watch_name ?? null;

  const latest = useQuery({
    queryKey: ['flights', 'latest', watch],
    queryFn: () =>
      getJson<LatestResponse>(`/api/flights/latest?watch=${encodeURIComponent(watch!)}`),
    enabled: !!watch,
  });

  const [filterToken, setFilterToken] = useState<{
    watch: string | null;
    selection: FlightFilterState | null;
  }>({ watch: null, selection: null });

  // Reset filters whenever the selected watch changes. Doing this during render
  // (conditionally) avoids a setState-in-effect lint error and a cascading render.
  if (filterToken.watch !== watch) {
    setFilterToken({ watch, selection: null });
  }

  const filterState: FlightFilterState | null = (() => {
    if (!latest.data?.available) return null;
    if (filterToken.selection == null) return defaultFilterState(latest.data.filters);
    return reconcileFilterState(filterToken.selection, latest.data.filters);
  })();

  const setFilterState = (next: FlightFilterState) => {
    setFilterToken({ watch, selection: next });
  };

  const filteredGroups = useMemo<CheapestShapeGroup[]>(() => {
    if (!latest.data?.available || !filterState) return [];
    const filtered = filterFlightRows(latest.data.rows, filterState);
    return groupFilteredRows(filtered, latest.data.per_shape);
  }, [latest.data, filterState]);

  const freshness = useMemo(
    () =>
      latest.data?.available
        ? deriveRunFreshness(latest.data.rows.map((r) => r.observed_at))
        : null,
    [latest.data],
  );

  const latestTotal = latest.data?.available ? latest.data.rows.length : 0;
  const filteredTotal = filteredGroups.reduce((sum, g) => sum + g.count, 0);

  const movers = useQuery({
    queryKey: ['flights', 'movers', watch],
    queryFn: () =>
      getJson<{ available: boolean; movers: FlightMover[]; threshold: number }>(
        `/api/flights/movers?watch=${encodeURIComponent(watch!)}`,
      ),
    enabled: !!watch,
  });

  const pairs = useQuery({
    queryKey: ['flights', 'pairs', watch],
    queryFn: () =>
      getJson<{ available: boolean; pairs: FlightPair[] }>(
        `/api/flights/pairs?watch=${encodeURIComponent(watch!)}`,
      ),
    enabled: !!watch,
  });

  const pairRows = useMemo(
    () => (pairs.data?.available ? pairs.data.pairs : []),
    [pairs.data],
  );

  const [pairSel, setPairSel] = useState<string | null>(null);
  const pair = useMemo(() => {
    if (pairSel) {
      const found = pairRows.find((p) => pairKey(p) === pairSel);
      if (found) return found;
    }
    return pairRows[0] ?? null;
  }, [pairRows, pairSel]);

  const series = useQuery({
    queryKey: ['flights', 'series', watch, pair?.origin, pair?.destination, pair?.outbound_date, pair?.inbound_date],
    queryFn: () =>
      getJson<{ available: boolean; rows: FlightObservation[] }>(
        `/api/flights/series?watch=${encodeURIComponent(watch!)}` +
          `&origin=${pair!.origin}&destination=${pair!.destination}` +
          `&outbound_date=${pair!.outbound_date}` +
          (pair!.inbound_date ? `&inbound_date=${pair!.inbound_date}` : ''),
      ),
    enabled: !!watch && !!pair,
  });

  const chartSeries: PriceSeries[] = useMemo(() => {
    const rows = series.data?.rows ?? [];
    const grouped = new Map<string, PriceSeries>();
    for (const r of rows) {
      if (r.price === null) continue;
      const label =
        `${r.carrier} ${tripShape(r)}` +
        (r.stopover_point ? ` via ${r.stopover_point}` : '');
      if (!grouped.has(label)) grouped.set(label, { label, points: [] });
      grouped.get(label)!.points.push({ observed_at: r.observed_at, price: r.price });
    }
    return Array.from(grouped.values());
  }, [series.data]);

  if (summary.isLoading) {
    return <p className="p-6 text-sm text-gray-500">Loading flight tracker…</p>;
  }
  if (summary.data && !summary.data.available) {
    return (
      <div className="p-6 max-w-xl">
        <h1 className="text-lg font-semibold mb-2">Flight tracker</h1>
        <p className="text-sm text-gray-600">
          Tracker database not found on this machine. Run the tracker once
          (<code className="font-mono text-xs">flight-tracker run --once</code> in
          <code className="font-mono text-xs"> ~/repos/flight-tracker</code>) and reload.
        </p>
        <p className="mt-2 text-xs text-gray-400 font-mono">{summary.data.reason}</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {watches.length > 1 && (
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={watch ?? ''}
            onChange={(e) => setWatchSel(e.target.value)}
          >
            {watches.map((w) => (
              <option key={w.watch_name} value={w.watch_name}>{w.watch_name}</option>
            ))}
          </select>
        )}
        {watch && (
          <span className="text-xs text-gray-500">
            {watches.find((w) => w.watch_name === watch)?.observations ?? 0} observations
            since {watches.find((w) => w.watch_name === watch)?.first_observed?.slice(0, 10)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card title="Cheapest itineraries (latest observation per series)">
          {latest.data?.available && filterState && (
            <div className="mb-4">
              <FlightFilterBar
                options={latest.data.filters}
                value={filterState}
                onChange={setFilterState}
                matched={filteredTotal}
                total={latestTotal}
              />
            </div>
          )}
          <CheapestItinerariesTable
            groups={filteredGroups}
            emptyMessage={filterState ? 'No itineraries match the current filters.' : undefined}
            freshness={freshness}
          />
        </Card>
        <Card title={`Movers (≥ CHF ${movers.data?.threshold ?? 25} since previous observation)`}>
          <FlightMoversList movers={movers.data?.movers ?? []} threshold={movers.data?.threshold ?? 25} />
        </Card>
      </div>

      <Card title="Price history">
        {pairRows.length > 0 && pair && (
          <FlightPairSelect
            pairs={pairRows}
            value={pairKey(pair)}
            onChange={setPairSel}
          />
        )}
        <FlightPriceChart series={chartSeries} />
      </Card>

      <Card title="Tracker run health">
        <FlightRunHealth runs={summary.data?.runs ?? []} />
      </Card>
    </div>
  );
}
